/**
 * 時間前入札 CSV アップロード画面 Step1「ファイル選択」パネル内の記入ガイド表
 * (common.md AC-DUC-037)。
 *
 * **文言の正本は spec `bidding/intraday-csv-import.md` §3「画面表示（記入ガイド表）」
 * の表**。 実装側で独自の説明文を持たない — 文言変更は spec の更新をもって行う。
 * 括弧のみ design 規約 (`design/CLAUDE.md` 括弧は半角 + 前に半角スペース1つ) に
 * 合わせて半角化する (供給地点マスタの記入ガイドと同じ既定方針)。 列順は
 * `INTRADAY_ORDER_CSV_HEADER` と完全一致させる (test で固定)。
 */
import type { ColumnGuideEntry } from '~/models/columnGuide'

export const INTRADAY_ORDER_CSV_COLUMN_GUIDE: ColumnGuideEntry[] = [
  {
    name: '受渡日',
    required: 'required',
    description: '入札する受渡日',
    rule: '「YYYY/MM/DD」の形式で入力。1 つのファイルに複数の受渡日を入力できる。受付開始 (受渡日の前日 12:00) より前の受渡日は入力できない。',
    example: '2026/09/01',
  },
  {
    name: 'コマ',
    required: 'required',
    description: '入札する 30 分コマの番号',
    rule: '「1〜48」の数字で入力 (1 = 00:00〜00:30、 48 = 23:30〜24:00)。取込の時点で GC (受渡コマ開始の 1 時間前) を過ぎたコマの行は取り込まれない。',
    example: '17',
  },
  {
    name: '発注主体区分',
    required: 'required',
    description: '入札する発注主体',
    rule: '「需要BG / 発電 / 自己託送BG」のいずれかを入力。行ごとに異なる区分を入力できる。同じ行のエリアとの組み合わせで、広域機関に自社のコードが登録されていない行は取り込まれない。',
    example: '需要BG',
  },
  {
    name: 'エリア',
    required: 'required',
    description: '入札するエリア',
    rule: '「北海道 / 東北 / 東京 / 中部 / 北陸 / 関西 / 中国 / 四国 / 九州」のいずれかを入力。',
    example: '東京',
  },
  {
    name: '売買',
    required: 'required',
    description: '自社から見た売買の方向',
    rule: '「買」または「売」を入力。',
    example: '買',
  },
  {
    name: '価格種別',
    required: 'required',
    description: '価格を指定するか (指値)、 指定しないか (成行)',
    rule: '「指値」または「成行」を入力。成行は、入札制限で成行注文を許可しているエリアのみ入力可能。',
    example: '指値',
  },
  {
    name: '価格 (円/kWh)',
    required: 'conditional',
    description: '売買したい価格 (円/kWh)',
    rule: '価格種別が指値の場合は必須。0.01〜999 の範囲で、小数点以下 2 桁まで入力。成行の場合は空欄にする。手数料自動考慮が OFF の場合は、入力した価格を入札価格として JEPX に入札する。ON の場合は、手数料を含めて売買したい価格 (希望価格) を入力する。JEPX へは手数料を加減算した入札価格で入札し、入札価格が 0.01〜999 の範囲または入札制限の上下限価格を外れる行は取り込めない。',
    example: '10.55',
  },
  {
    name: '基準価格',
    required: 'optional',
    description: '準備中の機能 (市場価格に合わせて入札価格を決める) で使う列',
    rule: '空欄にする。値を入れた行は取り込めない。',
    example: '',
  },
  {
    name: '基準価格エリア',
    required: 'optional',
    description: '準備中の機能で使う列',
    rule: '空欄にする。値を入れた行は取り込めない。',
    example: '',
  },
  {
    name: '演算子',
    required: 'optional',
    description: '準備中の機能で使う列',
    rule: '空欄にする。値を入れた行は取り込めない。',
    example: '',
  },
  {
    name: '調整値',
    required: 'optional',
    description: '準備中の機能で使う列',
    rule: '空欄にする。値を入れた行は取り込めない。',
    example: '',
  },
  {
    name: '数量 (MW)',
    required: 'required',
    description: '入札する数量 (MW)',
    rule: '0.1〜9999.9 の範囲で、小数点以下 1 桁まで入力。入札制限の上限数量の範囲内で入力。',
    example: '10.5',
  },
  {
    name: '自動取消時刻',
    required: 'optional',
    description: '入札を自動で取り消す予定の時刻',
    rule: '「YYYY/MM/DD HH:MM」の形式で入力。現在時刻より後で、その行のコマの GC (受渡コマ開始の 1 時間前) までの時刻を入力。空欄の場合は、入札制限画面のデフォルト取り下げ時刻が入る (その時刻を過ぎている行は取り込めない)。',
    example: '2026/09/01 06:30',
  },
  {
    name: 'JEPX自由記述欄',
    required: 'optional',
    description: 'JEPX へ送信される備考',
    rule: '70文字以内で入力。',
    example: '東京需要分',
  },
  {
    name: 'メモ',
    required: 'optional',
    description: '社内向けのメモ (JEPX へは送信しない)',
    rule: '100文字以内で入力。',
    example: '需要上振れ分を補填',
  },
]

/**
 * 英語表示の記入ガイド表 (spec §3「表示言語が英語のとき」、 文言の正本は spec
 * 「付録: 表示文言（英語）」の第3節の行、 #9332 問 2)。 列名は §2 の英語ヘッダー名、
 * 区分値の記入例は英語の値。 ブロック入札 CSV と同じく、 言語でガイド行ごと選ぶ。
 * 列順・必須区分は日本語の表と同じ (test で固定)。
 */
export const INTRADAY_ORDER_CSV_COLUMN_GUIDE_EN: ColumnGuideEntry[] = [
  {
    name: 'DeliveryDate',
    required: 'required',
    description: 'Delivery date to bid for',
    rule: 'Enter in YYYY/MM/DD format. One file can contain multiple delivery dates. Delivery dates before bidding opens (12:00 on the day before the delivery date) cannot be entered.',
    example: '2026/09/01',
  },
  {
    name: 'Slot',
    required: 'required',
    description: 'Number of the 30-minute slot to bid for',
    rule: 'Enter a number from 1 to 48 (1 = 00:00-00:30, 48 = 23:30-24:00). Rows for slots past gate closure (1 hour before the slot starts) at the time of import are not imported.',
    example: '17',
  },
  {
    name: 'IssuerType',
    required: 'required',
    description: 'Issuer that places the bid',
    rule: "Enter one of Demand BG / Generation / Self-wheeling BG. Each row can have a different issuer type. Rows for which your company's code is not registered in OCCTO for the combination with the area in the same row are not imported.",
    example: 'Demand BG',
  },
  {
    name: 'Area',
    required: 'required',
    description: 'Area to bid in',
    rule: 'Enter one of Hokkaido / Tohoku / Tokyo / Chubu / Hokuriku / Kansai / Chugoku / Shikoku / Kyushu.',
    example: 'Tokyo',
  },
  {
    name: 'BuySell',
    required: 'required',
    description: "Buy/sell direction from your company's side",
    rule: 'Enter Buy or Sell.',
    example: 'Buy',
  },
  {
    name: 'OrderType',
    required: 'required',
    description: 'Whether to specify a price ("Limit") or not ("Market")',
    rule: 'Enter "Limit" or "Market". "Market" can be entered only for areas where market orders are allowed in the bid limits.',
    example: 'Limit',
  },
  {
    name: 'OrderPrice',
    required: 'conditional',
    description: 'Price at which you want to buy or sell (JPY/kWh)',
    rule: 'Required for limit orders. Enter a value from 0.01 to 999, with up to 2 decimal places. Leave blank for market orders. When automatic fee adjustment is OFF, the price you enter is placed with JEPX as the order price. When it is ON, enter the price you want to trade at including the fee (the desired price). The bid is placed with JEPX at the order price, which is the desired price with the fee added or subtracted, and rows where the order price is outside 0.01 to 999 or outside the upper and lower price limits in the bid limits cannot be imported.',
    example: '10.55',
  },
  {
    name: 'ReferencePrice',
    required: 'optional',
    description:
      'Column for a feature that is not available yet (setting the order price based on the market price)',
    rule: 'Leave blank. Rows with a value cannot be imported.',
    example: '',
  },
  {
    name: 'ReferenceArea',
    required: 'optional',
    description: 'Column for a feature that is not available yet',
    rule: 'Leave blank. Rows with a value cannot be imported.',
    example: '',
  },
  {
    name: 'FormulaOperator',
    required: 'optional',
    description: 'Column for a feature that is not available yet',
    rule: 'Leave blank. Rows with a value cannot be imported.',
    example: '',
  },
  {
    name: 'FormulaValue',
    required: 'optional',
    description: 'Column for a feature that is not available yet',
    rule: 'Leave blank. Rows with a value cannot be imported.',
    example: '',
  },
  {
    name: 'Quantity',
    required: 'required',
    description: 'Quantity to bid (MW)',
    rule: 'Enter a value from 0.1 to 9999.9, with up to 1 decimal place. Enter a value within the maximum quantity in the bid limits.',
    example: '10.5',
  },
  {
    name: 'AutoCancelTime',
    required: 'optional',
    description: 'Time at which the bid is automatically canceled',
    rule: "Enter in YYYY/MM/DD HH:MM format. Enter a time later than the current time and no later than GC (1 hour before the start of the delivery slot) for that row's slot. If left blank, the default withdrawal time on the Bid limits screen is used (rows where that time has already passed cannot be imported).",
    example: '2026/09/01 06:30',
  },
  {
    name: 'JEPXFreeText',
    required: 'optional',
    description: 'Note sent to JEPX',
    rule: 'Enter up to 70 characters.',
    example: 'For Tokyo demand',
  },
  {
    name: 'Memo',
    required: 'optional',
    description: 'Internal memo (not sent to JEPX)',
    rule: 'Enter up to 100 characters.',
    example: 'Cover demand above forecast',
  },
]

/** 表示言語に応じた記入ガイド表 (spec §3)。 */
export function intradayCsvColumnGuide(isEnglish: boolean): ColumnGuideEntry[] {
  return isEnglish
    ? INTRADAY_ORDER_CSV_COLUMN_GUIDE_EN
    : INTRADAY_ORDER_CSV_COLUMN_GUIDE
}
